import { useTranslation } from "react-i18next";
import { Download, Eye, Pencil } from "lucide-react";
import { Badge } from "catalyst-ui-kit/badge";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import { btnSlimCls } from "components/tw/classes";
import { API_URL } from "constants.js";
import useIsPhone from "features/files/use-is-phone";
import { formatPeriodMonth } from "features/billing-period/billing-period";
import SendStatusBadge from "features/invoice-send/send-status-badge";
import { formatInvoicePublicNumber, getInvoiceFileId } from "utils/invoice-number";
import type { Invoice } from "types/Invoice";

type Props = {
  invoices: Invoice[];
  companyId?: string;
  onOpen: (invoiceId: string) => void;
};

export function InvoiceStatusBadge({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation();
  return invoice.status === "draft" ? (
    <Badge color="amber">{t("draft", { defaultValue: "Draft" })}</Badge>
  ) : (
    <Badge color="green">
      {t("invoice_status_confirmed", { defaultValue: "Confirmed" })}
    </Badge>
  );
}

/**
 * The invoices of one order on a phone, one card each: number, amount, billed
 * month and status, with whether it was emailed. Tapping the card opens the
 * invoice; the PDF has a button of its own. The due date is only in the table.
 */
function OrderInvoiceCards({ invoices, companyId, onOpen }: Props) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-2">
      {invoices.map((invoice) => {
        const isDraft = invoice.status === "draft";
        const fileId = getInvoiceFileId(invoice);
        return (
          <li
            key={invoice.id}
            className="flex items-stretch overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-700"
          >
            <button
              type="button"
              className="min-h-11 min-w-0 flex-1 space-y-1 p-3 text-left"
              onClick={() => onOpen(invoice.id)}
            >
              <span className="flex items-baseline justify-between gap-2 text-sm">
                <span className="font-medium">
                  {formatInvoicePublicNumber(invoice) ||
                    (isDraft ? t("draft", { defaultValue: "Draft" }) : "—")}
                </span>
                <span className="font-medium tabular-nums">
                  <Currency value={invoice._amountWithVat} isSymbol />
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-500">
                <span>{formatPeriodMonth(invoice.periodFrom) || "—"}</span>
                <InvoiceStatusBadge invoice={invoice} />
                {invoice.type !== "erp" && (
                  <Badge color="blue">
                    {t("invoice_type_finvalda", { defaultValue: "Finvalda" })}
                  </Badge>
                )}
                {!isDraft && invoice.type === "erp" && <SendStatusBadge send={invoice.send} />}
              </span>
            </button>
            {fileId && (
              <a
                href={`${API_URL}/${companyId}/files/download/${fileId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-12 shrink-0 items-center justify-center border-l border-zinc-200 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300"
                aria-label={t("download_invoice", { defaultValue: "Download invoice PDF" })}
              >
                <Download className="size-4" aria-hidden="true" />
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The invoices of one order, one row each. */
export default function OrderInvoicesTable({ invoices, companyId, onOpen }: Props) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  if (isPhone) {
    return <OrderInvoiceCards invoices={invoices} companyId={companyId} onOpen={onOpen} />;
  }

  const headerCls = "px-2 py-1.5 font-medium";
  const cellCls = "whitespace-nowrap px-2 py-1.5";

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-left text-zinc-500 dark:border-zinc-700">
            <th className={headerCls}>{t("invoice_number_short", { defaultValue: "No." })}</th>
            <th className={headerCls}>{t("invoice_period_column")}</th>
            <th className={headerCls}>{t("date")}</th>
            <th className={headerCls}>{t("invoice_due_column", { defaultValue: "Due" })}</th>
            <th className={`${headerCls} text-right`}>
              {t("totalWithVat", { defaultValue: "Total with VAT" })}
            </th>
            <th className={headerCls}>{t("status", { defaultValue: "Status" })}</th>
            <th className={headerCls}>{t("invoice_send_column", { defaultValue: "Emailing" })}</th>
            <th className={headerCls} aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => {
            const isDraft = invoice.status === "draft";
            const fileId = getInvoiceFileId(invoice);
            return (
              <tr
                key={invoice.id}
                className="border-b border-zinc-100 last:border-0 dark:border-zinc-800"
              >
                <td className={`${cellCls} font-medium`}>
                  {formatInvoicePublicNumber(invoice) ||
                    (isDraft ? t("draft", { defaultValue: "Draft" }) : "—")}
                </td>
                <td className={cellCls}>{formatPeriodMonth(invoice.periodFrom) || "—"}</td>
                <td className={cellCls}>
                  {invoice.date ? <PrettyDate dateString={invoice.date} /> : "—"}
                </td>
                <td className={cellCls}>
                  {invoice.paymentDueDate ? <PrettyDate dateString={invoice.paymentDueDate} /> : "—"}
                </td>
                <td className={`${cellCls} text-right`}>
                  <Currency value={invoice._amountWithVat} isSymbol />
                </td>
                <td className={cellCls}>
                  <div className="flex items-center gap-1">
                    <InvoiceStatusBadge invoice={invoice} />
                    {/* An order can carry both its ERP invoice and the Finvalda copy of it. */}
                    {invoice.type !== "erp" && (
                      <Badge color="blue">
                        {t("invoice_type_finvalda", { defaultValue: "Finvalda" })}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  {/* Only ERP invoices that are numbered can be emailed from here.
                      Unlike the other cells this one may wrap: its sent line is long. */}
                  {!isDraft && invoice.type === "erp" ? (
                    <SendStatusBadge send={invoice.send} showDetails />
                  ) : (
                    "—"
                  )}
                </td>
                <td className={cellCls}>
                  <div className="flex items-center justify-end gap-1.5">
                    {fileId && (
                      <a
                        href={`${API_URL}/${companyId}/files/download/${fileId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={btnSlimCls}
                        title={t("download_invoice", { defaultValue: "Download invoice PDF" })}
                      >
                        <Download className="size-3.5" aria-hidden="true" />
                      </a>
                    )}
                    <button
                      type="button"
                      className={btnSlimCls}
                      title={
                        isDraft
                          ? t("edit_invoice", { defaultValue: "Edit invoice" })
                          : t("view_invoice", { defaultValue: "View invoice" })
                      }
                      onClick={() => onOpen(invoice.id)}
                    >
                      {isDraft ? (
                        <Pencil className="size-3.5" aria-hidden="true" />
                      ) : (
                        <Eye className="size-3.5" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
